// Prep-notification scheduling — clean rebuild.
//
// CONTRACT
//
// A recipe's prep step says "leadMinutes: 30" meaning "30 minutes
// before the cook starts." Period. That's it. Everything else falls
// out of that one rule.
//
// Two kinds of scheduled meals:
//
//   COOK-ANCHORED (default): scheduled_for IS the cook start time.
//     prep deliver_at = scheduled_for − leadMinutes.
//     Example: cook 9:00 AM, prep 30m → prep at 8:30 AM.
//
//   EAT-ANCHORED (eat_anchor === true): scheduled_for is the EAT time.
//     We backdate the cook start by recipe duration, then subtract
//     leadMinutes from THAT.
//     prep deliver_at = scheduled_for − cookDuration − leadMinutes.
//     Example: eat 3:00 PM, cook 60m, prep 30m
//       → cook starts at 2:00 PM
//       → prep at 1:30 PM.
//
// One source-of-truth helper — `cookStartFor(meal, recipe)` — handles
// the anchor branching. Callers (this scheduler, the calendar tile,
// MealDetailDrawer's "Start cooking" line) all use it. There is NO
// other place where "cook start time" gets recomputed.
//
// Recipes with overnight bake steps add a synthetic morning push (Day
// 2 wake-up). That stays handled by computeOvernightMorningRow at the
// bottom — it's a separate concept from the prep-lead-time math above.
//
// All idempotent: syncPrepNotifications deletes the meal's existing
// rows and inserts fresh, so reschedule / toggle / servings change
// always produces correct deliver_at values.

import { supabase } from "./supabase";
import {
  getOvernightDurations,
  OVERNIGHT_DAY2_START_HOUR,
  pickDurationMin,
} from "./calendarUtils";

// ── Lead-time parsing ────────────────────────────────────────────
// Tolerant: numbers pass through, strings like "T-30m" / "2h" / "1d"
// get normalized to minutes. Unknown / malformed → 0 (the caller
// drops zero-lead entries below, so a typo can't fire at epoch).
export function parseLeadTime(input) {
  if (typeof input === "number" && Number.isFinite(input)) return Math.max(0, input);
  if (!input) return 0;
  const s = String(input).trim().toLowerCase();
  if (s === "overnight") return 12 * 60;
  if (s === "same-day")  return 4  * 60;
  const m = s.match(/^t?-?\s*(\d+)\s*([mhd])$/);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n)) return 0;
  const unit = m[2];
  if (unit === "m") return n;
  if (unit === "h") return n * 60;
  if (unit === "d") return n * 24 * 60;
  return 0;
}

// ── Prep-step resolution ─────────────────────────────────────────
// Normalize the recipe's prep metadata into a flat list of
// { key, leadMinutes, title, body, emoji, defaultOn, source }.
//
// Three sources, descending priority:
//   1. recipe.prepSteps   — structured shape, preferred.
//   2. recipe.prepNotifications — legacy shape (text-only leadTime).
//   3. fallback — recipe with time.prep but no authored steps gets
//      a generic "Start prepping" reminder so every meal has at least
//      one ping. Without this, recipes that don't author prepSteps
//      get zero notifications.
export function resolvePrepSteps(recipe) {
  if (!recipe) return [];
  const out = [];
  const seen = new Set();

  for (const s of recipe.prepSteps || []) {
    const key = s.id || s.key;
    if (!key || seen.has(key)) continue;
    const leadMinutes = typeof s.leadMinutes === "number"
      ? s.leadMinutes
      : parseLeadTime(s.leadTime);
    if (leadMinutes <= 0) continue;
    out.push({
      key,
      leadMinutes,
      title: s.title || "Prep reminder",
      body:  s.body || s.text || "",
      emoji: s.emoji || (leadMinutes >= 6 * 60 ? "🧊" : "⏰"),
      defaultOn: s.defaultOn !== false,
      source: s.source
        || (leadMinutes >= 6 * 60 ? "freeze_overnight" : "recipe_prep"),
    });
    seen.add(key);
  }

  for (const n of recipe.prepNotifications || []) {
    const key = n.id;
    if (!key || seen.has(key)) continue;
    const leadMinutes = parseLeadTime(n.leadTime);
    if (leadMinutes <= 0) continue;
    out.push({
      key,
      leadMinutes,
      title: "Prep reminder",
      body:  n.text || "",
      emoji: "⏰",
      defaultOn: n.defaultOn !== false,
      source: "recipe_prep",
    });
    seen.add(key);
  }

  if (out.length === 0 && recipe.time?.prep) {
    const prep = Number(recipe.time.prep) || 0;
    const leadMinutes = prep + 5;
    out.push({
      key: "start-prep",
      leadMinutes,
      title: "Start prepping " + (recipe.title || "your meal"),
      body:  "Pull everything out, read the recipe top-to-bottom before the burners go on.",
      emoji: "🥕",
      defaultOn: true,
      source: "recipe_prep",
    });
  }

  return out;
}

// ── Per-meal toggle gate ─────────────────────────────────────────
// scheduled_meals.notification_settings is a JSONB toggle map (key →
// bool). True / false override the recipe's defaultOn; missing keys
// fall through to defaultOn.
export function prepEnabled(notificationSettings, step) {
  const v = notificationSettings?.[step.key];
  if (v === true) return true;
  if (v === false) return false;
  return !!step.defaultOn;
}

// ── COOK START (single source of truth) ──────────────────────────
// Returns the Date when cooking actually begins for this meal. This
// is the anchor every prep-lead-time calculation uses. Same answer
// the calendar tile renders, same answer MealDetailDrawer shows.
//
// Recipe authors write leadMinutes meaning "X minutes before cook
// starts" — never "before eating." This function bakes the eat-anchor
// backdate into one place so callers never re-derive.
export function cookStartFor(scheduledMeal, recipe) {
  if (!scheduledMeal?.scheduled_for) return null;
  const t = new Date(scheduledMeal.scheduled_for);
  if (Number.isNaN(t.getTime())) return null;

  // Cook-anchored: scheduled_for IS the cook start. Done.
  if (!scheduledMeal.eat_anchor) return t;

  // Eat-anchored: backdate by recipe duration. Overnight bakes have
  // their own multi-day duration math; everything else uses the
  // single pickDurationMin total.
  const overnight = getOvernightDurations(recipe);
  const durationMin = overnight
    ? (overnight.day1ActiveMin + overnight.overnightMin + overnight.day2Min)
    : pickDurationMin(recipe, 30);

  return new Date(t.getTime() - durationMin * 60_000);
}

// ── Sync entry point ─────────────────────────────────────────────
// Idempotent: deletes the meal's existing prep_notifications rows,
// then inserts fresh rows for every enabled prep step. Returns the
// number of rows queued. Fire-and-forget; any failure is logged but
// never propagates back to the meal-scheduling flow.
export async function syncPrepNotifications({ scheduledMeal, recipe, userId }) {
  if (!scheduledMeal?.id || !recipe || !userId) return 0;
  try {
    const cookStart = cookStartFor(scheduledMeal, recipe);
    if (!cookStart) {
      // No cook anchor → nothing to schedule. Still atomically clear
      // any leftover rows so a meal that LOSES its cookStart (eat-
      // anchor flipped, recipe duration zeroed) doesn't keep stale
      // prep tiles hanging around.
      await supabase.rpc("replace_prep_notifications", {
        p_meal_id: scheduledMeal.id,
        p_rows: [],
      });
      return 0;
    }
    const cookStartMs = cookStart.getTime();

    const steps    = resolvePrepSteps(recipe);
    const settings = scheduledMeal.notification_settings || {};

    // Per-step row builder. Single line of math:
    //   deliver_at = cookStart − leadMinutes
    // Same formula whether the meal is cook-anchored or eat-anchored
    // — the difference is absorbed into cookStart by cookStartFor().
    const rows = steps
      .filter(s => prepEnabled(settings, s))
      .map(s => ({
        user_id:           userId,
        recipe_slug:       scheduledMeal.recipe_slug,
        prep_key:          s.key,
        title:             s.title,
        body:              s.body,
        emoji:             s.emoji,
        lead_minutes:      s.leadMinutes,
        deliver_at:        new Date(cookStartMs - s.leadMinutes * 60_000).toISOString(),
        source:            s.source,
      }));

    // Overnight Day-2 morning push. Independent of the prep-lead-time
    // math above — fires at the Day 2 cook anchor regardless of any
    // leadMinutes lattice. Only emitted for recipes with an overnight
    // step. Strip scheduled_meal_id from the row shape since the RPC
    // takes p_meal_id as its own argument and stamps every row with it.
    const overnightRow = computeOvernightMorningRow({ scheduledMeal, recipe, userId });
    if (overnightRow) {
      const { scheduled_meal_id: _omit, ...rest } = overnightRow;
      rows.push(rest);
    }

    // Single atomic call — DELETE + INSERT in one transaction so the
    // realtime subscription emits ONE state-change to subscribers
    // instead of a delete-burst followed by an insert-burst that
    // briefly shows the union as duplicate prep tiles. The RPC also
    // bypasses the per-user RLS DELETE filter, so a meal whose cook
    // is a different family member gets its old rows cleaned even
    // when the scheduling user can't see them. See migration 0162.
    const { data, error } = await supabase.rpc("replace_prep_notifications", {
      p_meal_id: scheduledMeal.id,
      p_rows: rows,
    });
    if (error) {
      console.warn("[prepScheduler] replace RPC failed:", error);
      return 0;
    }
    return Number.isFinite(Number(data)) ? Number(data) : rows.length;
  } catch (e) {
    console.warn("[prepScheduler] sync threw:", e);
    return 0;
  }
}

// ── Overnight bake — Day 2 morning push ──────────────────────────
// Independent of the prep-lead-time math because the morning anchor
// is itself an absolute time, not a "X minutes before something"
// offset. Caller appends this row to the rest if non-null.
function computeOvernightMorningRow({ scheduledMeal, recipe, userId }) {
  const dur = getOvernightDurations(recipe);
  if (!dur) return null;
  const scheduledForMs = new Date(scheduledMeal.scheduled_for).getTime();
  if (!Number.isFinite(scheduledForMs)) return null;

  let deliverAtMs;
  if (scheduledMeal.eat_anchor) {
    // Day 2 ends at eat time → starts day2Min before that.
    deliverAtMs = scheduledForMs - dur.day2Min * 60_000;
  } else {
    // Cook-anchored: anchor at user's day2_hour or default (8 AM).
    const hour = Number.isFinite(scheduledMeal.day2_hour)
      ? Math.floor(scheduledMeal.day2_hour)
      : OVERNIGHT_DAY2_START_HOUR;
    const d = new Date(scheduledForMs);
    d.setDate(d.getDate() + 1);
    d.setHours(hour, 0, 0, 0);
    deliverAtMs = d.getTime();
  }

  if (deliverAtMs <= Date.now()) return null;

  return {
    user_id:           userId,
    scheduled_meal_id: scheduledMeal.id,
    recipe_slug:       scheduledMeal.recipe_slug,
    prep_key:          "overnight-morning",
    title:             `Day 2 of ${recipe.title || "your bake"}`,
    body:              "Time to shape and bake — your dough is ready.",
    emoji:             "🌅",
    lead_minutes:      0,
    deliver_at:        new Date(deliverAtMs).toISOString(),
    source:            "overnight_morning",
  };
}
